// Ekran 14 — Ayarlar.
// Yerleşim ref_files/btys-erp-14-ayarlar.html'den: .crumb · .page-h · .seg(5)
// + sekmeye göre kart(lar). §8-14: yalnız tesis_sorumlusu (403 diğerleri) —
// kapı sunucuda (YetkiFiltresi), buradaki YetkiKapisi kullanıcıyı boş ekranla
// baş başa bırakmamak için.

import { useEffect, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { tr, trGiris, sayiyaCevirBos } from '../../lib/trFormat'
import { YetkiKapisi } from '../../lib/yetki'
import EsikTablosu from './EsikTablosu'
import KullanicilarSekmesi from './KullanicilarSekmesi'
import { ayarlar as ayarApi, type TesisAyarlari } from './api'

type Sekme = 'genel' | 'esik' | 'kul' | 'intg' | 'den'

const SEKMELER: [Sekme, string][] = [
  ['genel', 'Genel'],
  ['esik', 'Eşik & Tolerans'],
  ['kul', 'Kullanıcılar & Roller'],
  ['intg', 'Entegrasyonlar'],
  ['den', 'Denetim & Yedek'],
]

function hataMetni(h: unknown): string {
  if (h instanceof ApiHatasi) {
    return h.detay?.length ? `${h.message} — ${h.detay.join(' · ')}` : h.message
  }
  return 'İşlem tamamlanamadı.'
}

// -------------------------------------------------------------- Genel sekmesi

function GenelSekmesi() {
  const kuyruk = useQueryClient()
  const sorgu = useQuery({ queryKey: ['ayar-tesis'], queryFn: ayarApi.tesis })

  const [form, setForm] = useState<Record<string, string>>({})

  useEffect(() => {
    const a = sorgu.data
    if (!a) return
    setForm({
      tesisAd: a.tesisAd,
      kuruluGuc: trGiris(a.kuruluGuc),
      lisansNo: a.lisansNo ?? '',
      cr1: trGiris(a.cr1Hacim),
      cr2: trGiris(a.cr2Hacim),
      sc1: trGiris(a.sc1Hacim),
      sifreOmruGun: trGiris(a.sifreOmruGun),
    })
  }, [sorgu.data])

  const kaydet = useMutation({
    mutationFn: (a: TesisAyarlari) =>
      ayarApi.tesisYaz({
        tesisAd: form.tesisAd ?? a.tesisAd,
        kuruluGuc: sayiyaCevirBos(form.kuruluGuc ?? ''),
        lisansNo: form.lisansNo || null,
        cr1Hacim: sayiyaCevirBos(form.cr1 ?? ''),
        cr2Hacim: sayiyaCevirBos(form.cr2 ?? ''),
        sc1Hacim: sayiyaCevirBos(form.sc1 ?? ''),
        etaEl: a.etaEl,
        saatDilimi: a.saatDilimi,
        paraBirimi: a.paraBirimi,
        sifreOmruGun: sayiyaCevirBos(form.sifreOmruGun ?? ''),
      }),
    onSuccess: async (yeni) => {
      kuyruk.setQueryData(['ayar-tesis'], yeni)
      // V_hedef değişmiş olabilir: besleme raporu ve panel bandı bayatlar.
      await Promise.all([
        kuyruk.invalidateQueries({ queryKey: ['besleme'] }),
        kuyruk.invalidateQueries({ queryKey: ['panel'] }),
      ])
      const v = Object.values(yeni.vHedef).reduce((a, b) => a + b, 0)
      toast(`Tesis bilgileri kaydedildi — denetim izine işlendi · V_hedef ${tr(v, 0)} m³`)
    },
    onError: (h) => toast(hataMetni(h)),
  })

  const a = sorgu.data
  if (sorgu.isLoading || !a) {
    return <div className="card"><div className="insp-empty">Ayarlar yükleniyor…</div></div>
  }

  const alan = (
    anahtar: string,
    etiket: string,
    ekstra?: { mono?: boolean; ipucu?: string },
  ) => (
    <div className="fld" key={anahtar}>
      <label htmlFor={`ay-${anahtar}`}>{etiket}</label>
      <input
        id={`ay-${anahtar}`}
        className={ekstra?.mono ? 'in mono' : 'in'}
        value={form[anahtar] ?? ''}
        onChange={(e) => setForm((f) => ({ ...f, [anahtar]: e.target.value }))}
      />
      {ekstra?.ipucu && <div className="fld-hint">{ekstra.ipucu}</div>}
    </div>
  )

  const vHedefToplam = Object.values(a.vHedef).reduce((x, y) => x + y, 0)

  return (
    <div className="card">
      <div className="sect-t">Tesis Bilgileri</div>
      <div style={{ padding: 12 }}>
        <div className="fgrid">
          <div className="fld">
            <label htmlFor="ay-sirket">Şirket</label>
            <input id="ay-sirket" className="in" value={a.sirket} disabled />
          </div>
          {alan('tesisAd', 'Tesis adı')}
          {alan('kuruluGuc', 'Kurulu güç (kWe)', { mono: true })}
          {alan('lisansNo', 'Lisans / EPDK no', { mono: true })}
          {alan('cr1', 'CR-1 hacim (m³)', { mono: true })}
          {alan('cr2', 'CR-2 hacim (m³)', { mono: true })}
          {alan('sc1', 'SC-1 hacim (m³)', {
            mono: true,
            ipucu: 'V_hedef’e girmez — son çürütücü taze substrat almaz (K10).',
          })}
          <div className="fld">
            <label htmlFor="ay-eta">Kojen elektrik verimi η</label>
            <input id="ay-eta" className="in mono" value={trGiris(a.etaEl)} disabled />
            <div className="fld-hint">
              §6.1 sabiti (ETA_EL) — hesap motoru bunu koddan okur, değiştirilemez.
            </div>
          </div>
          <div className="fld">
            <label htmlFor="ay-tz">Saat dilimi</label>
            <select id="ay-tz" className="in" value="ist" disabled>
              <option value="ist">Europe/Istanbul (UTC+3)</option>
            </select>
          </div>
          <div className="fld">
            <label htmlFor="ay-para">Para birimi / sayı biçimi</label>
            <select id="ay-para" className="in" value="try" disabled>
              <option value="try">TRY · 1.234,56</option>
            </select>
          </div>
          {alan('sifreOmruGun', 'Şifre yenileme önerisi (gün)', { mono: true })}
        </div>

        <div className="pnote" style={{ marginTop: 8 }}>
          Çürütücü hacimleri hesap motorunun <b>V_hedef</b> girdisidir (OLR/HRT — §6.2);
          değişiklik geriye dönük beyanları etkilemez, yeni günlerde geçerlidir. Şu anki
          V_hedef: <b>{tr(vHedefToplam, 0)} m³</b> (CR-1 + CR-2; SC-1 dahil değil).
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
          <button
            className="btn btn-pri btn-sm"
            type="button"
            disabled={kaydet.isPending}
            onClick={() => kaydet.mutate(a)}
          >
            Kaydet
          </button>
        </div>
      </div>
    </div>
  )
}

// -------------------------------------------------------- Entegrasyonlar

// Her kalemin kendi ikonu var (referans 595/597/599/601): dördü de aynı zincir
// ikonuyla çizilirse kartlar birbirinden ayırt edilemez, göz hangi satırda
// olduğunu simgeden bulamaz.
const ENTEGRASYONLAR: { baslik: string; rozet: string; aciklama: string; ikon: ReactNode }[] = [
  {
    baslik: 'Kantar Donanımı',
    rozet: 'F2 · Bağlı değil',
    aciklama:
      'Seri/TCP köprüsüyle brüt-dara otomatik okunur; v1’de manuel giriş (K7).',
    ikon: (
      <>
        <path d="M1 3h15v13H1z" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
      </>
    ),
  },
  {
    baslik: 'SCADA / Sensör Akışı',
    rozet: 'F6 · Planlandı',
    aciklama:
      'GA/GD/KJ değerlerinin beyan gridine otomatik akışı; manuel giriş fallback kalır.',
    ikon: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 1v4M12 19v4M4.2 4.2l2.8 2.8M17 17l2.8 2.8M1 12h4M19 12h4" />
      </>
    ),
  },
  {
    baslik: 'EPİAŞ · Taahhüt & Uzlaştırma',
    rozet: 'F6 · Planlandı',
    aciklama:
      'Saatlik taahhüt çekilir; dengesizlik piyasa fiyatıyla maliyetlendirilir (K41).',
    ikon: <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />,
  },
  {
    baslik: 'e-İrsaliye / e-Fatura',
    rozet: 'F6 · Planlandı',
    aciklama: 'SVK sevk belgeleri ve cari ekstre entegrasyonu.',
    ikon: (
      <>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <path d="M14 2v6h6" />
      </>
    ),
  },
]

function EntegrasyonSekmesi() {
  return (
    <div className="card">
      <div className="sect-t">
        Entegrasyonlar
        <span className="cell-sub" style={{ marginLeft: 6 }}>
          faz planı §10
        </span>
      </div>
      <div className="intg">
        {ENTEGRASYONLAR.map((e) => (
          <div className="ic" key={e.baslik}>
            <span className="ii">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {e.ikon}
              </svg>
            </span>
            {/* flex:1 — .ic bir flex kabı; metin sarmalı kalan genişliği alsın
                ki uzun açıklamalar ikonun yanında sıkışmasın (referans 596). */}
            <div style={{ flex: 1 }}>
              <b>{e.baslik}</b>{' '}
              <span className="badge b-amber" style={{ marginLeft: 4 }}>
                {e.rozet}
              </span>
              <div className="is">{e.aciklama}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="pnote">
        Bu sekme salt-okunur: dördünün de arkasında henüz uç yok, düğme
        konulsaydı hiçbir şey yapmazdı.
      </div>
    </div>
  )
}

// ------------------------------------------------------- Denetim & Yedek

function DenetimSekmesi() {
  return (
    <div className="card">
      <div className="sect-t">Denetim &amp; Yedekleme</div>
      <div style={{ padding: 12 }}>
        <div className="fgrid">
          <div className="fld">
            <label htmlFor="dn-sakla">Denetim izi saklama</label>
            <select id="dn-sakla" className="in" value="10" disabled>
              <option value="10">10 yıl (mevzuat)</option>
            </select>
          </div>
          <div className="fld">
            <label htmlFor="dn-yedek">Otomatik yedek</label>
            <select id="dn-yedek" className="in" value="gunluk" disabled>
              <option value="gunluk">Günlük 03:00 · 30 kopya</option>
            </select>
          </div>
        </div>

        <div className="info-box" style={{ marginTop: 10 }}>
          <div>
            <b>K15 · K39 hatırlatması:</b> Onaylı kayıtlar ve stok hareketleri hiçbir rol
            tarafından silinemez; düzeltmeler storno/revizyon üretir ve orijinal kayıt soluk
            görünürlükle saklanır.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button
            className="btn btn-sec btn-sm"
            type="button"
            disabled
            title="Faz 7 — denetim izi Serilog satırlarından üretilecek"
          >
            Denetim izini dışa aktar
          </button>
          <button
            className="btn btn-sec btn-sm"
            type="button"
            disabled
            title="Faz 7 — yedekleme sunucu tarafında kurulacak"
          >
            Şimdi yedek al
          </button>
        </div>
        <div className="pnote" style={{ marginTop: 8 }}>
          Saklama ve yedek ayarları v1’de sabittir; düğmeler Faz 7’de bağlanacak.
          Referansın “dışa aktarıldı” toast’ı taşınmadı — arkasında bir iş olmadan
          yan etki bildirmek yanlış olurdu.
        </div>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------- sayfa

function Icerik() {
  const [parametreler, setParametreler] = useSearchParams()
  const sekme = (parametreler.get('sekme') as Sekme) || 'genel'

  // .pane sarmalını Kabuk içindeki SekmePanolari zaten basıyor; burada bir tane
  // daha açmak referanstaki tek katmanı (main.content > section.pane.on) ikiye
  // çıkarıyordu — bu yüzden fragment.
  return (
    <>
      <div className="crumb">
        Yönetim <b>/ Ayarlar</b>
      </div>
      <div className="page-h">
        <h1 className="page-t">Ayarlar &amp; Yönetim</h1>
        <div className="page-actions">
          <span className="badge b-navy">
            <span className="dot" />
            Yalnız yetkili roller
          </span>
        </div>
      </div>

      <div className="seg" style={{ marginBottom: 8 }}>
        {SEKMELER.map(([k, etiket]) => (
          <button
            key={k}
            className={sekme === k ? 'seg-i on' : 'seg-i'}
            type="button"
            onClick={() => setParametreler(k === 'genel' ? {} : { sekme: k })}
          >
            {etiket}
          </button>
        ))}
      </div>

      {sekme === 'genel' && <GenelSekmesi />}
      {sekme === 'esik' && <EsikTablosu />}
      {sekme === 'kul' && <KullanicilarSekmesi />}
      {sekme === 'intg' && <EntegrasyonSekmesi />}
      {sekme === 'den' && <DenetimSekmesi />}
    </>
  )
}

export default function AyarlarSayfasi() {
  return (
    <Kabuk listeBasligi="Ayarlar & Yönetim">
      {/* Yetki kapısı sayfanın İÇİNDE: kabuk görünür kalsın ki kullanıcı
          nereye düştüğünü anlasın ve başka modüle geçebilsin (§8-14). */}
      <YetkiKapisi modul="Ayarlar" gereken="yaz">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
